import {
  AddressFormElement,
  AutocompleteFormElement,
  CheckboxFormElement,
  DateFormElement,
  FieldDefinitionFormElement,
  FormDefinitionType,
  FormElementType,
  FormExecutionType,
  HeadingFormElement,
  MultiSelectionFormElement,
  NestedFormElement,
  NestedFormElementExecution,
  RadioFormElement,
  SelectionFormElement,
  TaskExecutionState,
  TextFormElement,
  UploadFormElement,
} from "../../../types";
import { FormExecutionComponentProps } from "./FormExecutionComponent";
import {
  TaskCheckboxFormComponent,
  TaskMultiSelectFormComponent,
  TaskRadioFormComponent,
  TaskSelectionFormComponent,
  TaskTextFormComponent,
  TaskUploadFormComponent,
} from "./task-form-elements";
import TaskAutocompleteFormComponent from "./task-form-elements/TaskAutocompleteFormComponent";
import TaskDateFormComponent from "./task-form-elements/TaskDateFormComponent";
import TaskFieldDefinitionFormComponent from "./task-form-elements/TaskFieldDefinitionFormComponent";
import TaskHeadingFormComponent from "./task-form-elements/TaskHeadingFormComponent";
import TaskNestedFormComponent from "./task-form-elements/TaskNestedFormComponent";
import TaskFormAddressComponent from "./TaskFormAddressComponent";

const getCommonFormExecutionComponent = ({
  element,
  trueElements,
  index,
  state,
  formElementExecution,
  disabled = false,
  nested,
  viewSize,
  validElements,
  ...props
}: {
  element: FormDefinitionType;
  trueElements: FormDefinitionType[];
  index: number;
  state: TaskExecutionState | undefined;
  formElementExecution?: FormExecutionType;
  disabled?: boolean;
  nested?: boolean;
  validElements?: number[];
} & FormExecutionComponentProps) => {
  switch (element.type) {
    case FormElementType.Heading:
      return (
        <TaskHeadingFormComponent
          {...(element as HeadingFormElement)}
          nested={nested}
          key={element.id}
          disabled={disabled}
          removeNestedComponent={props.removeNestedForm}
          taskExecutionState={state}
          skipped={
            validElements &&
            validElements.length > 0 &&
            props.task.data?.isWizard &&
            !validElements.includes(index)
          }
        />
      );
    case FormElementType.Text:
      return (
        <TaskTextFormComponent
          {...(element as TextFormElement)}
          formId={props.taskExecution?.id}
          key={element.id + "+" + element.value}
          disabled={disabled}
          taskExecutionState={state}
          onValueChange={(value) => {
            (trueElements[index] as TextFormElement).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
        />
      );
    case FormElementType.Address:
      return (
        <TaskFormAddressComponent
          {...(element as AddressFormElement)}
          disabled={disabled}
          key={element?.id + "+" + element?.value}
          taskExecutionState={state}
          execution={formElementExecution}
          onValueChange={(value) => {
            (trueElements[index] as AddressFormElement).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
            if (
              value.first_line == "" ||
              value.city == "" ||
              value.state == "" ||
              value.zip_code == 0
            ) {
              (trueElements[index] as AddressFormElement).value = undefined;
            }
          }}
        />
      );
    case FormElementType.Radio:
      return (
        <TaskRadioFormComponent
          {...(element as RadioFormElement)}
          formId={props.task?.id}
          key={element.id}
          disabled={disabled}
          taskExecutionState={state}
          onValueChange={(value) => {
            (trueElements[index] as RadioFormElement).selection = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
          formExecutionComponentProps={props}
        />
      );
    case FormElementType.MultiSelection:
      return (
        <TaskMultiSelectFormComponent
          {...(element as MultiSelectionFormElement)}
          formId={props.task?.id}
          key={element.id}
          taskExecutionState={state}
          disabled={disabled}
          onValueChange={(value) => {
            (trueElements[index] as MultiSelectionFormElement).selection =
              value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
        />
      );
    case FormElementType.Selection:
      return (
        <TaskSelectionFormComponent
          {...(element as SelectionFormElement)}
          formId={props.task?.id}
          key={element.id}
          taskExecutionState={state}
          onValueChange={(value) => {
            (trueElements[index] as SelectionFormElement).selection = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
          disabled={disabled}
        />
      );
    case FormElementType.Upload:
      return (
        <TaskUploadFormComponent
          {...(element as UploadFormElement)}
          formId={props.task?.id}
          key={element.id}
          taskExecutionState={state}
          onValueChange={(value) => {
            (trueElements[index] as UploadFormElement).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
          disabled={disabled}
          size={viewSize}
        />
      );
    case FormElementType.NestedForm:
      return (
        <TaskNestedFormComponent
          {...props}
          {...(element as NestedFormElement)}
          disabled={disabled}
          taskExecutionState={state}
          execution={formElementExecution as NestedFormElementExecution}
          onButtonClick={(value) => {
            (trueElements[index] as NestedFormElementExecution).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          executionElements={
            (formElementExecution as NestedFormElementExecution)?.elements
          }
          key={element.id}
        />
      );
    case FormElementType.Date:
      return (
        <TaskDateFormComponent
          {...(element as DateFormElement)}
          key={element.id}
          taskExecutionState={state}
          disabled={disabled}
          onValueChange={(value) => {
            (trueElements[index] as DateFormElement).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
        />
      );
    case FormElementType.Checkbox:
      return (
        <TaskCheckboxFormComponent
          {...(element as CheckboxFormElement)}
          key={element.id}
          taskExecutionState={state}
          disabled={disabled}
          onValueChange={(value) => {
            (trueElements[index] as CheckboxFormElement).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
          formExecutionComponentProps={props}
        />
      );
    case FormElementType.FieldDefinition:
      return (
        <TaskFieldDefinitionFormComponent
          {...(element as FieldDefinitionFormElement)}
          key={element.id + JSON.stringify(element.value)}
          taskExecutionState={state}
          disabled={disabled}
          onValueChange={(newValue) => {
            const valueIndex = (
              (trueElements[index] as FieldDefinitionFormElement).value ?? []
            ).findIndex((e) => e.id == newValue.elementId);

            if (valueIndex >= 0) {
              (trueElements[index] as FieldDefinitionFormElement).value![
                valueIndex
              ] = newValue.value;
            } else {
              (trueElements[index] as FieldDefinitionFormElement).value = [
                ...((trueElements[index] as FieldDefinitionFormElement).value ??
                  []),
                newValue.value,
              ];
            }

            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
          formExecutionComponentProps={props}
        />
      );
    case FormElementType.Autocomplete:
      return (
        <TaskAutocompleteFormComponent
          {...(element as AutocompleteFormElement)}
          formId={props.task?.id}
          key={element.id}
          disabled={disabled}
          onValueChange={(value) => {
            (trueElements[index] as AutocompleteFormElement).value = value;
            if (props.onValueChange) {
              props.onValueChange(props.task);
            }
          }}
          execution={formElementExecution}
        />
      );
    default:
      return;
  }
};

export default getCommonFormExecutionComponent;
